import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../config/axios.config';
import ConsentCheckbox from './input/ConsentCheckbox';
import ReportBanner from './ReportBanner';

// Page-level composition for "submit one or more files (+ optional text fields)
// → render output" reports — Face Match/Liveness/Extract, EsignSetBranding, etc.
// Multipart/form-data submission. SKILL hard rule 4 (no new CSS) honored —
// pure PrimeReact + PrimeFlex inside the helper.

const FileTile = ({ field, file, onChange, error, loading }) => {
    const inputRef = useRef(null);
    const helpId = `${field.name}-help`;
    return (
        <div className={`field ${field.colClass || 'col-12 md:col-6'}`}>
            <label htmlFor={field.name} className="block font-medium text-sm mb-2">
                {field.label}{field.required !== false && <span className="text-red-500"> *</span>}
            </label>
            <input
                ref={inputRef}
                id={field.name}
                type="file"
                accept={field.accept || 'image/*,application/pdf'}
                disabled={loading}
                onChange={(e) => onChange(field.name, e.target.files?.[0] || null)}
                style={{ display: 'none' }}
                aria-describedby={helpId}
                aria-invalid={!!error}
            />
            <div className={classNames('surface-100 border-round border-1 p-3 flex flex-column gap-2', error ? 'surface-border-red' : 'surface-border')}>
                <div className="flex align-items-center justify-content-between gap-2">
                    <div className="flex align-items-center gap-2 min-w-0 flex-1">
                        <i className={`pi ${file ? 'pi-check-circle text-green-500' : 'pi-cloud-upload text-primary'}`} aria-hidden="true" />
                        <span className="text-sm text-700 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {file ? file.name : 'No file selected'}
                        </span>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                        <Button
                            type="button"
                            icon="pi pi-folder-open"
                            label={file ? 'Change' : 'Choose'}
                            size="small"
                            outlined
                            disabled={loading}
                            onClick={() => inputRef.current?.click()}
                        />
                        {file && (
                            <Button
                                type="button"
                                icon="pi pi-times"
                                size="small"
                                severity="secondary"
                                outlined
                                aria-label={`Clear ${field.label}`}
                                disabled={loading}
                                onClick={() => onChange(field.name, null)}
                            />
                        )}
                    </div>
                </div>
                {file && (
                    <div className="flex align-items-center gap-2">
                        <Tag severity="info" value={file.type || 'file'} className="text-xs" />
                        <span className="text-xs text-500">{(file.size / 1024).toFixed(1)} KB</span>
                    </div>
                )}
                {field.hint && <span className="text-xs text-500">{field.hint}</span>}
            </div>
            {error && (
                <small id={helpId} className="p-error block mt-1" role="alert">
                    <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                    {error}
                </small>
            )}
        </div>
    );
};

const FileUploadReportForm = ({
    banner,
    fileFields = [],
    textFields = [],
    endpoint,
    consentReportType,
    submitLabel = 'Submit',
    successSummary = 'Done',
    errorSummary = 'Failed',
    outputComponent: OutputComponent,
    outputProps = {},
    confirmHeader = 'Confirm submission',
    confirmAcceptLabel = 'Yes, submit',
}) => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const buildInitialText = () => {
        const init = { consent: false };
        textFields.forEach((f) => { init[f.name] = f.default ?? ''; });
        return init;
    };
    const buildInitialFiles = () => {
        const init = {};
        fileFields.forEach((f) => { init[f.name] = null; });
        return init;
    };

    const [formData, setFormData] = useState(buildInitialText);
    const [files, setFiles] = useState(buildInitialFiles);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [serviceDetails, setServiceDetails] = useState(null);
    const [fetchingService, setFetchingService] = useState(true);

    useEffect(() => {
        if (!serviceId) { setFetchingService(false); return; }
        const load = async () => {
            setFetchingService(true);
            try {
                const res = await axiosInstance.get(`/user/report-services/${serviceId}`);
                if (res.data.success && res.data.data) setServiceDetails(res.data.data);
            } catch (error) {
                console.error('Load service details failed:', error?.message || 'Unknown error');
                setServiceDetails({ available_credits: 'N/A', credits_required: 'N/A' });
            } finally {
                setFetchingService(false);
            }
        };
        load();
    }, [serviceId]);

    const handleTextChange = (field) => (e) => {
        const raw = e.target ? e.target.value : e.value;
        let v = raw ?? '';
        if (typeof v === 'string') {
            if (field.transform) v = field.transform(v);
            else if (field.uppercase) v = v.toUpperCase().trim();
            else if (field.lowercase) v = v.toLowerCase().trim();
            if (field.maxLength) v = v.slice(0, field.maxLength);
        }
        setFormData((prev) => ({ ...prev, [field.name]: v }));
        if (errors[field.name]) setErrors((prev) => ({ ...prev, [field.name]: '' }));
    };

    const handleFileChange = (name, file) => {
        setFiles((prev) => ({ ...prev, [name]: file }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleConsentChange = (e) => {
        setFormData((prev) => ({ ...prev, consent: e.checked }));
        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
    };

    const validate = () => {
        const next = {};
        fileFields.forEach((f) => {
            if (f.required === false) return;
            if (!files[f.name]) next[f.name] = `${f.label} is required`;
            else if (f.maxSizeBytes && files[f.name].size > f.maxSizeBytes) {
                next[f.name] = `${f.label} exceeds ${(f.maxSizeBytes / 1024 / 1024).toFixed(1)}MB`;
            }
        });
        textFields.forEach((f) => {
            if (f.required === false) return;
            const v = formData[f.name];
            if (!v || (typeof v === 'string' && !v.trim())) next[f.name] = `${f.label} is required`;
            else if (f.regex && !f.regex.test(v)) next[f.name] = f.regexError || `Invalid ${f.label}`;
        });
        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const submit = async () => {
        setLoading(true);
        try {
            const fd = new FormData();
            fileFields.forEach((f) => { if (files[f.name]) fd.append(f.bodyKey || f.name, files[f.name]); });
            textFields.forEach((f) => {
                if (formData[f.name] !== undefined && formData[f.name] !== '') fd.append(f.bodyKey || f.name, formData[f.name]);
            });
            fd.append('api_provider_service_id', String(parseInt(serviceId, 10)));
            if (endpoint.extraBody) {
                Object.entries(endpoint.extraBody).forEach(([k, v]) => fd.append(k, v));
            }

            const res = await axiosInstance.post(endpoint.path, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (!res.data.success) throw new Error(res.data.message || errorSummary);
            setReportData(res.data);
            toast.current?.show({ severity: 'success', summary: successSummary, detail: 'Report ready below', life: 2000 });
            try {
                const refreshed = await axiosInstance.get(`/user/report-services/${serviceId}`);
                if (refreshed.data.success && refreshed.data.data) setServiceDetails(refreshed.data.data);
            } catch (refreshErr) {
                console.error('Credit refresh failed:', refreshErr?.message || 'Unknown error');
            }
        } catch (error) {
            console.error(`${errorSummary}:`, error?.message || 'Unknown error');
            const status = error.response?.status;
            const map = {
                500: 'Internal server error. Please try again later.',
                422: 'Please check your inputs and try again.',
                429: 'Too many requests. Please wait a minute before trying again.',
                400: 'Invalid input. Please verify and retry.',
                413: 'File too large. Please choose a smaller file.',
            };
            let detail = map[status] || error.response?.data?.message || error.message || errorSummary;
            if (status === 402) {
                const c = error.response?.data || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            }
            toast.current?.show({ severity: 'error', summary: errorSummary, detail, life: 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    {fileFields.filter((f) => files[f.name]).map((f) => (
                        <div key={f.name} className="mb-2"><span className="text-600">{f.label}:</span> <span className="text-900 font-medium">{files[f.name].name}</span></div>
                    ))}
                    {textFields.filter((f) => formData[f.name]).map((f) => (
                        <div key={f.name} className="mb-2"><span className="text-600">{f.label}:</span> <span className="text-900 font-medium">{String(formData[f.name])}</span></div>
                    ))}
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: confirmHeader,
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: confirmAcceptLabel,
            rejectLabel: 'Cancel',
            accept: submit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '500px' },
            draggable: false,
        });
    };

    const handleAnother = () => {
        setReportData(null);
        setFormData(buildInitialText());
        setFiles(buildInitialFiles());
        setErrors({});
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon={banner.icon}
                        title={banner.title}
                        subtitle={banner.subtitle}
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid">
                                    {fileFields.map((f) => (
                                        <FileTile
                                            key={f.name}
                                            field={f}
                                            file={files[f.name]}
                                            onChange={handleFileChange}
                                            error={errors[f.name]}
                                            loading={loading}
                                        />
                                    ))}
                                    {textFields.map((f) => {
                                        const err = errors[f.name];
                                        const helpId = `${f.name}-help`;
                                        return (
                                            <div key={f.name} className={`field ${f.colClass || 'col-12 md:col-6'}`}>
                                                <label htmlFor={f.name} className="block font-medium text-sm mb-2">
                                                    {f.label}{f.required !== false && <span className="text-red-500"> *</span>}
                                                </label>
                                                <IconField iconPosition="left" className="w-full">
                                                    {f.icon && <InputIcon className={`pi ${f.icon}`} />}
                                                    <InputText
                                                        id={f.name}
                                                        value={formData[f.name] || ''}
                                                        onChange={handleTextChange(f)}
                                                        placeholder={f.placeholder}
                                                        maxLength={f.maxLength}
                                                        autoCapitalize={f.autoCapitalize}
                                                        spellCheck={f.spellCheck === false ? false : undefined}
                                                        disabled={loading}
                                                        className={classNames('w-full p-inputtext-sm', f.mono && 'font-mono', { 'p-invalid': !!err })}
                                                        aria-describedby={helpId}
                                                        aria-invalid={!!err}
                                                    />
                                                </IconField>
                                                {err && (
                                                    <small id={helpId} className="p-error block mt-1" role="alert">
                                                        <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                                        {err}
                                                    </small>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <ConsentCheckbox
                                    checked={formData.consent}
                                    onChange={handleConsentChange}
                                    error={errors.consent}
                                    reportType={consentReportType}
                                />
                                <div className="flex justify-content-end mt-3">
                                    <Button
                                        type="submit"
                                        label={submitLabel}
                                        icon="pi pi-arrow-right"
                                        iconPos="right"
                                        loading={loading}
                                        disabled={loading}
                                        size="small"
                                    />
                                </div>
                            </form>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button
                                    label="Submit another"
                                    icon="pi pi-refresh"
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    onClick={handleAnother}
                                />
                            </div>
                            <OutputComponent reportData={reportData} {...outputProps} />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default FileUploadReportForm;
